/**
 * <mb-vinilo>: vista previa del vinilo (caligrafía blanca) sobre la foto de un tarro.
 *
 * Lo pinta `snippets/mb-vinilo.liquid` (con su CSS). Lo usan el hero de la portada y la ficha del tarro.
 * El vinilo real se diseña a mano: esto es una vista previa orientativa (nombre grande, dedicatoria más pequeña y adornos).
 *
 * - `campo`: id del <input> del nombre (eventos `input` y `pageshow`).
 * - `campo-dedicatoria`: id del <input> de la dedicatoria (opcional).
 * - `adornos`: conjunto de adornos: corazones (por defecto), estrellas, chuches, halloween o ninguno.
 * - `ejemplo` / `ejemplo-dedicatoria`: textos de ayuda atenuados mientras no hay nada escrito.
 * - También se puede manejar a mano con las propiedades `nombre` y `dedicatoria`.
 * - Todo se escribe con `textContent` (nunca como HTML) y sin emojis, porque el vinilo no los imprime. Los adornos son SVG propios.
 * - Tamaños autoajustados por longitud en cqw: el contenedor debe tener `position: relative` y `container-type: inline-size`.
 */

export const LARGO_NOMBRE = 18;
export const LARGO_DEDICATORIA = 40;

const EMOJI = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}️‍⃣]/gu;

/**
 * Quita emojis y marcas de emoji.
 * @param {string} valor
 */
export const quitarEmoji = (valor) => valor.replace(EMOJI, '');

/**
 * Texto tal y como se imprimirá: sin emojis, espacios simples y recortado.
 * @param {string} valor
 */
export const limpiarFrase = (valor) => quitarEmoji(valor).replace(/\s+/g, ' ').trim();

/** Dibujos (SVG propios, trazo/relleno currentColor): [viewBox, contenido]. */
const SIMBOLOS = {
  corazon: [
    '0 0 24 24',
    '<path fill="currentColor" d="M12 21S4.5 16.4 2.4 11.8C.9 8.3 3 4.5 6.5 4.5c2 0 3.7 1.1 5.5 3.2 1.8-2.1 3.5-3.2 5.5-3.2 3.5 0 5.6 3.8 4.1 7.3C19.5 16.4 12 21 12 21z"/>',
  ],
  brillo: [
    '0 0 24 24',
    '<path fill="currentColor" d="M12 1c.8 5.6 2.4 9.2 11 11-8.6 1.8-10.2 5.4-11 11-.8-5.6-2.4-9.2-11-11C9.6 10.2 11.2 6.6 12 1z"/>',
  ],
  estrella: [
    '0 0 24 24',
    '<path fill="currentColor" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" d="M12 2.5l2.8 6 6.5.8-4.8 4.5 1.3 6.5L12 17l-5.8 3.3 1.3-6.500L2.7 9.300l6.500-.8z"/>',
  ],
  caramelo: [
    '0 0 28 20',
    '<g fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="14" cy="10" r="5.2"/><path d="M9.3 8L2.5 4.5v11L9.3 12M18.7 8l6.8-3.5v11L18.7 12M11.5 7.8c1.6 1.2 3.4 4.2 5 4.4"/></g>',
  ],
  floritura: [
    '0 0 40 20',
    '<path fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" d="M2 15c5 4.5 10 3.5 10-1.5 0-3.8-5.2-3.5-5 .2.2 3.5 6.5 3.2 10.5-.7C21 9 24 6.5 28 8c3.5 1.4 3.2 5.6.2 5.5-2.800-.1-2.500-3.600.3-3.800 3-.2 6.200 2 9.500 5"/>',
  ],
  murcielago: [
    '0 0 32 16',
    '<path fill="currentColor" d="M16 2.5l2 3h2.5c2.300-2.800 6.500-4 11-3-2 1.200-3 3.200-2.500 5.300-1.600-1-3.400-1.100-5 .2-1.100-1-2.600-1.100-3.700 0-.9 2.200-1.900 5.500-3.800 5.500s-2.900-3.300-3.800-5.500c-1.100-1.100-2.600-1-3.700 0-1.600-1.300-3.400-1.200-5-.2C3 5.700 2 3.700 0 2.500c4.500-1 8.700.2 11 3H14z"/>',
  ],
  calabaza: [
    '0 0 24 24',
    '<path fill="currentColor" fill-rule="evenodd" d="M12 7c-1.500-1.100-3-1.600-5-1.600-3.200 0-5.600 3-5.600 7s2.400 7 5.600 7c2 0 3.500-.5 5-1.600 1.500 1.100 3 1.600 5 1.600 3.200 0 5.600-3 5.600-7s-2.400-7-5.600-7c-2 0-3.500.5-5 1.600zM7.200 10.200l1.800 2.800H5.400zM16.800 10.200l1.800 2.800H15zM7.500 15.800h9l-1.200 2h-1.800l-.9-1.100-.9 1.100H8.700z"/><path fill="none" stroke="currentColor" stroke-width="1.800" stroke-linecap="round" d="M12 7c0-2 .6-3.300 2.200-4.500"/>',
  ],
  fantasma: [
    '0 0 24 24',
    '<path fill="currentColor" fill-rule="evenodd" d="M12 2C7.600 2 5 5.400 5 9.500V21l2.500-2 2.250 2L12 19l2.250 2 2.250-2 2.500 2V9.500C19 5.400 16.400 2 12 2zM9.400 8.600a1.200 1.600 0 1 0 0 3.200 1.200 1.600 0 0 0 0-3.200zM14.600 8.600a1.200 1.600 0 1 0 0 3.200 1.200 1.600 0 0 0 0-3.200zM10 13.800c.7 1.300 3.300 1.300 4 0z"/>',
  ],
};

/**
 * Conjuntos de adornos: [dibujo, x %, y %, ancho en cqw, giro en grados] (x e y = centro, en % de la caja del vinilo).
 * @type {Record<string, [keyof typeof SIMBOLOS, number, number, number, number][]>}
 */
const CONJUNTOS = {
  corazones: [
    ['corazon', 14, 19.6, 6.4, -16],
    ['corazon', 85.3, 13.5, 5, 14],
    ['corazon', 81.7, 86.5, 4, 12],
    ['brillo', 19.8, 12, 4.4, 0],
    ['brillo', 86, 48.5, 3.2, 0],
    ['floritura', 32.7, 88, 13, -4],
    ['floritura', 68.7, 12, 10, 176],
  ],
  estrellas: [
    ['brillo', 14, 21.1, 6.2, 0],
    ['brillo', 86, 16.6, 4.6, 0],
    ['estrella', 86, 88, 4.4, 14],
    ['brillo', 18.3, 88, 3.6, 0],
    ['estrella', 28.4, 12, 3.6, -12],
    ['brillo', 86, 50, 3, 0],
    ['floritura', 64.4, 88, 12, 2],
  ],
  chuches: [
    ['caramelo', 14, 18.1, 8.4, -20],
    ['caramelo', 85.3, 88, 7.4, 16],
    ['caramelo', 86, 24.2, 5.6, 22],
    ['brillo', 22.6, 88, 4.2, 0],
    ['brillo', 50, 12, 3.6, 0],
    ['floritura', 34.2, 88, 12, -3],
    ['floritura', 65.8, 12, 10, 177],
  ],
  halloween: [
    ['murcielago', 15.4, 13.5, 8.4, -10],
    ['murcielago', 84.6, 88, 7, 8],
    ['calabaza', 86, 19.6, 6.4, 12],
    ['calabaza', 14, 88, 5.4, -10],
    ['fantasma', 78.8, 12, 4.6, 10],
    ['brillo', 50, 88, 3.4, 0],
    ['floritura', 64.4, 12, 9, 176],
  ],
};

const NS = 'http://www.w3.org/2000/svg';

/** Los adornos se agrandan y se acercan al texto respecto a las coordenadas de CONJUNTOS (ajuste fino en un solo sitio). */
const ESCALA_ADORNOS = 1.5;
const ACERCAR_X = 0.9;
const ACERCAR_Y = 1;

/**
 * @param {keyof typeof SIMBOLOS} nombre
 * @returns {SVGElement}
 */
function crearSimbolo(nombre) {
  const [caja, contenido] = SIMBOLOS[nombre];
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', caja);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', 'mb-vinilo__adorno');
  // Contenido propio y constante (nunca entrada del usuario)
  svg.innerHTML = contenido;
  return svg;
}

/**
 * Tamaño (cqw) para que un texto de `largo` caracteres quepa en `ancho` cqw.
 * @param {number} largo
 * @param {number} maximo
 * @param {number} minimo
 * @param {number} ancho
 * @param {number} cuerpo  ancho medio de un carácter, en em
 */
function ajustar(largo, maximo, minimo, ancho, cuerpo) {
  return Math.max(minimo, Math.min(maximo, ancho / (Math.max(largo, 1) * cuerpo)));
}

/**
 * Reparte la dedicatoria en 1-2 líneas (una sola hasta 20 caracteres; si no, se parte por el espacio más cercano al centro).
 * @param {string} texto
 */
function partirLineas(texto) {
  if (texto.length <= 20 || !texto.includes(' ')) return [texto];
  const centro = texto.length / 2;
  let mejor = -1;
  for (let i = 0; i < texto.length; i++) {
    if (texto[i] === ' ' && (mejor === -1 || Math.abs(i - centro) < Math.abs(mejor - centro))) mejor = i;
  }
  return [texto.slice(0, mejor), texto.slice(mejor + 1)];
}

class MbVinilo extends HTMLElement {
  static observedAttributes = ['ejemplo', 'ejemplo-dedicatoria', 'adornos'];

  /** @type {AbortController | null} */
  #controller = null;
  /** @type {AbortController | null} */
  #controllerCampos = null;
  /** @type {HTMLElement | null} */
  #nombreEl = null;
  /** @type {HTMLElement | null} */
  #dedicatoriaEl = null;
  /** @type {HTMLElement | null} */
  #adornosEl = null;
  #nombre = '';
  #dedicatoria = '';
  /** @type {string | null} */
  #conjuntoPintado = null;

  connectedCallback() {
    this.#nombreEl = this.#asegurar('[data-mb-vinilo-texto]', 'mb-vinilo__texto', 'data-mb-vinilo-texto');
    this.#dedicatoriaEl = this.#asegurar('[data-mb-vinilo-dedicatoria]', 'mb-vinilo__dedicatoria', 'data-mb-vinilo-dedicatoria');
    this.#adornosEl = this.#asegurar('[data-mb-vinilo-adornos]', 'mb-vinilo__adornos', 'data-mb-vinilo-adornos');

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Al volver con el botón "atrás" el navegador puede restaurar los valores sin lanzar "input"
    window.addEventListener('pageshow', () => this.#leerCampos(), { signal });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#enlazar(), { once: true, signal });
    } else {
      this.#enlazar();
    }

    this.#pintar();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controllerCampos?.abort();
    this.#controller = null;
    this.#controllerCampos = null;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#pintar();
  }

  /**
   * @param {string} selector
   * @param {string} clase
   * @param {string} marca
   * @returns {HTMLElement}
   */
  #asegurar(selector, clase, marca) {
    const existente = this.querySelector(selector);
    if (existente instanceof HTMLElement) return existente;
    const nuevo = document.createElement('span');
    nuevo.className = clase;
    nuevo.setAttribute(marca, '');
    this.append(nuevo);
    return nuevo;
  }

  /** Nombre escrito (sin limpiar). Vacío = se ve el ejemplo atenuado. */
  get nombre() {
    return this.#nombre;
  }

  set nombre(valor) {
    this.#nombre = String(valor ?? '');
    this.#pintar();
  }

  /** Dedicatoria escrita (sin limpiar). */
  get dedicatoria() {
    return this.#dedicatoria;
  }

  set dedicatoria(valor) {
    this.#dedicatoria = String(valor ?? '');
    this.#pintar();
  }

  /** @param {string | null} id */
  #campo(id) {
    const campo = id ? document.getElementById(id) : null;
    return campo instanceof HTMLInputElement || campo instanceof HTMLTextAreaElement ? campo : null;
  }

  /** Se engancha a los <input> indicados y sigue lo que se escribe. */
  #enlazar() {
    this.#controllerCampos?.abort();
    this.#controllerCampos = new AbortController();
    const { signal } = this.#controllerCampos;
    for (const atributo of ['campo', 'campo-dedicatoria']) {
      this.#campo(this.getAttribute(atributo))?.addEventListener('input', () => this.#leerCampos(), { signal });
    }
    this.#leerCampos();
  }

  #leerCampos() {
    this.#nombre = this.#campo(this.getAttribute('campo'))?.value ?? this.#nombre;
    this.#dedicatoria = this.#campo(this.getAttribute('campo-dedicatoria'))?.value ?? this.#dedicatoria;
    this.#pintar();
  }

  /** Muestra lo escrito (o el ejemplo atenuado si no hay nada), la dedicatoria en 1-2 líneas y los adornos. */
  #pintar() {
    if (!this.#nombreEl || !this.#dedicatoriaEl) return;
    const nombre = limpiarFrase(this.#nombre).slice(0, LARGO_NOMBRE);
    const dedicatoria = limpiarFrase(this.#dedicatoria).slice(0, LARGO_DEDICATORIA);
    const vacio = nombre === '';
    const textoNombre = vacio ? (this.getAttribute('ejemplo') ?? '') : nombre;
    // Con todo vacío se enseña la dedicatoria de ejemplo atenuada; con nombre escrito, solo lo que se haya escrito
    const ejemploDedicatoria = vacio && dedicatoria === '';
    const textoDedicatoria = ejemploDedicatoria ? (this.getAttribute('ejemplo-dedicatoria') ?? '') : dedicatoria;

    this.#nombreEl.textContent = textoNombre;
    this.#nombreEl.classList.toggle('mb-vinilo__texto--ejemplo', vacio);
    this.toggleAttribute('data-vacio', vacio);

    const lineas = textoDedicatoria === '' ? [] : partirLineas(textoDedicatoria);
    this.#dedicatoriaEl.replaceChildren(
      ...lineas.map((linea) => {
        const el = document.createElement('span');
        el.className = 'mb-vinilo__linea';
        el.textContent = linea;
        return el;
      })
    );
    this.#dedicatoriaEl.classList.toggle('mb-vinilo__texto--ejemplo', ejemploDedicatoria);

    // Con dedicatoria el nombre se reduce un poco para que todo quepa en la caja
    const maxNombre = lineas.length > 0 ? 15 : 16.5;
    this.style.setProperty('--mb-tamano', ajustar(textoNombre.length, maxNombre, 5.6, 47, 0.41).toFixed(2));
    const mayorLinea = lineas.reduce((mayor, linea) => Math.max(mayor, linea.length), 0);
    this.style.setProperty('--mb-tamano-dedicatoria', ajustar(mayorLinea, 8.2, 4.8, 47, 0.34).toFixed(2));

    this.#pintarAdornos();
  }

  #pintarAdornos() {
    if (!this.#adornosEl) return;
    const pedido = this.getAttribute('adornos') ?? 'corazones';
    const conjunto = pedido === 'ninguno' ? '' : pedido in CONJUNTOS ? pedido : 'corazones';
    if (conjunto === this.#conjuntoPintado) return;
    this.#conjuntoPintado = conjunto;

    const items = (CONJUNTOS[conjunto] ?? []).map(([dibujo, x, y, ancho, giro]) => {
      const svg = crearSimbolo(dibujo);
      const cx = 50 + (x - 50) * ACERCAR_X;
      const cy = 50 + (y - 50) * ACERCAR_Y;
      svg.style.cssText = `--x:${cx.toFixed(1)}%;--y:${cy.toFixed(1)}%;--s:${(ancho * ESCALA_ADORNOS).toFixed(1)};--r:${giro}deg`;
      return svg;
    });
    this.#adornosEl.replaceChildren(...items);
  }
}

if (!customElements.get('mb-vinilo')) {
  customElements.define('mb-vinilo', MbVinilo);
}
